import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Mic, Palette } from "lucide-react";

const NETWORKS = [
  {
    id: "vlmag",
    kicker: "01 — The Main Culture Platform",
    name: ["VL", "MAG"],
    accent: "text-[#E30613]",
    tagline: "Vision. Culture. Progress.",
    desc: "The central media network connecting fashion, music, art, sports, business and creative culture. VLMAG tells the complete story of the culture — documenting the creatives, brands, artists and entrepreneurs shaping the next generation.",
    covers: "Culture · Interviews · Documentaries · Events · Creative stories · Emerging talent · Original features",
    image: "https://images.pexels.com/photos/19218008/pexels-photo-19218008.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200",
    reverse: false,
    tint: false,
  },
  {
    id: "music",
    kicker: "02 — The Sound of the Culture",
    name: ["VLMAG ", "MUSIC"],
    accent: "text-[#E30613]",
    tagline: "New Sounds. Real Stories. Creative Energy.",
    desc: "The music and entertainment network dedicated to artists, DJs, producers and the people behind today's evolving sound. It gives new and established talent a platform to share their music, stories and upcoming projects.",
    covers: "New music · Artist interviews · Live performances · Listening events · DJ sets · Behind-the-scenes · Releases",
    image: "https://images.unsplash.com/photo-1541126274323-dbac58d14741?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwyfHxkaiUyMGxpdmUlMjBwZXJmb3JtYW5jZXxlbnwwfHx8fDE3ODQ1ODY3MTJ8MA&ixlib=rb-4.1.0&q=85",
    reverse: true,
    tint: true,
  },
  {
    id: "style",
    kicker: "03 — Fashion. Culture. Identity.",
    name: ["VLMAG ", "STYLE"],
    accent: "text-neutral-400",
    tagline: "Fashion. Culture. Identity.",
    desc: "The fashion and lifestyle network focused on personal style, streetwear, luxury fashion, emerging brands, designers and models. It explores how people use fashion to communicate who they are and what they represent.",
    covers: "Editorials · Brand features · Style reports · Trends · Collections · Models · Designers · Creative direction",
    image: "https://images.unsplash.com/photo-1606143412458-acc5f86de897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxlZGl0b3JpYWwlMjBmYXNoaW9uJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzg0NTg2NzEyfDA&ixlib=rb-4.1.0&q=85",
    reverse: false,
    tint: false,
  },
];

const MORE = [
  {
    id: "podcast",
    to: "/podcast",
    icon: Mic,
    badge: "Coming Soon",
    name: ["BEYOND ", "THE VISUAL"],
    desc: "The VLMAG Original podcast. Season 01 — CULTURED. Beyond what people create, to the story behind it.",
    cta: "Join The List",
    image: "https://static.prod-images.emergentagent.com/jobs/adb5decd-5900-4816-abd1-ea444c906724/images/3e3db35e2a6f322550cee2319949f2ac240218d00df08d840c5fa25f473336c8.jpeg",
  },
  {
    id: "art",
    to: "/art",
    icon: Palette,
    badge: "The Gallery",
    name: ["VLMAG ", "ART"],
    desc: "The Canvas of the Culture. Discover artists and collect exclusive, original pieces from the VLMAG gallery.",
    cta: "Enter The Gallery",
    image: "https://static.prod-images.emergentagent.com/jobs/adb5decd-5900-4816-abd1-ea444c906724/images/97a07e5e7450e48de75d480452fa64d02603d6af50c780db5ae60919cb27627c.jpeg",
  },
];

const MoreCard = ({ c }) => {
  const Icon = c.icon;
  return (
    <Link to={c.to} data-testid={`network-card-${c.id}`} className="group relative overflow-hidden aspect-[16/10] border border-white/10 hover:border-[#E30613] transition-colors duration-500 block">
      <img src={c.image} alt={c.name.join("")} className="absolute inset-0 w-full h-full object-cover grayscale-img opacity-50 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/20" />
      <div className="relative h-full flex flex-col justify-between p-6 md:p-8">
        <span className="inline-flex items-center gap-2 self-start bg-[#E30613] px-3 py-1.5 text-[9px] uppercase tracking-[0.22em] font-bold text-white">
          <Icon size={12} /> {c.badge}
        </span>
        <div>
          <h3 className="font-display uppercase leading-[0.85] tracking-tight text-4xl md:text-5xl">
            <span className="text-white">{c.name[0]}</span><span className="text-[#E30613]">{c.name[1]}</span>
          </h3>
          <p className="text-neutral-300 text-sm md:text-base leading-relaxed mt-3 max-w-md">{c.desc}</p>
          <span className="group/cta inline-flex items-center gap-2 mt-5 text-xs uppercase tracking-[0.2em] font-bold text-white">
            {c.cta}
            <ArrowUpRight size={16} className="text-[#E30613] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const NetworkBlock = ({ n }) => (
  <div
    id={n.id}
    data-testid={`network-${n.id}`}
    className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center border-t border-white/10 py-16 md:py-28`}
  >
    {/* Image */}
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className={`group relative overflow-hidden md:col-span-7 ${n.reverse ? "md:order-2" : "md:order-1"}`}
    >
      <div className="aspect-[4/3] md:aspect-[16/11] overflow-hidden">
        <img
          src={n.image}
          alt={n.name.join("")}
          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ${n.tint ? "" : "grayscale-img"}`}
        />
      </div>
      {n.tint && <div className="absolute inset-0 bg-[#E30613] mix-blend-multiply opacity-30 pointer-events-none group-hover:opacity-10 transition-opacity duration-700" />}
      <div className="absolute bottom-0 left-0 bg-black px-4 py-2 border-t border-r border-white/10">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-400">{n.kicker}</span>
      </div>
    </motion.div>

    {/* Text */}
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: 0.1 }}
      className={`md:col-span-5 ${n.reverse ? "md:order-1" : "md:order-2"}`}
    >
      <h3 className="font-display uppercase leading-[0.85] tracking-tight text-6xl md:text-7xl">
        <span className={n.id === "style" ? "text-white" : "text-[#E30613]"}>{n.name[0]}</span>
        <span className={n.id === "style" ? "text-neutral-500" : "text-white"}>{n.name[1]}</span>
      </h3>
      <p className="font-serif-ed italic text-2xl md:text-3xl text-white mt-4">{n.tagline}</p>
      <p className="text-neutral-300 leading-relaxed mt-6 text-base md:text-lg">{n.desc}</p>
      <div className="mt-7 pt-6 border-t border-white/10">
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-neutral-500 mb-2">What it covers</p>
        <p className="text-sm text-neutral-400 leading-relaxed">{n.covers}</p>
      </div>
      <a
        href="#featured"
        data-testid={`network-cta-${n.id}`}
        className="group inline-flex items-center gap-2 mt-8 text-xs uppercase tracking-[0.2em] font-bold text-white"
      >
        Be Part of {n.name.join("").trim()}
        <ArrowUpRight size={16} className="text-[#E30613] group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
      </a>
    </motion.div>
  </div>
);

export const Networks = () => (
  <section id="networks" data-testid="networks" className="px-5 md:px-10 py-16 md:py-24 relative">
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="max-w-4xl mb-6"
    >
      <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-4">One Network. Three Voices.</p>
      <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-5xl md:text-7xl">
        The Networks That<br />Build the Culture
      </h2>
    </motion.div>
    {NETWORKS.map((n) => (
      <NetworkBlock key={n.id} n={n} />
    ))}

    <div className="border-t border-white/10 pt-16 md:pt-24" data-testid="more-from-vlmag">
      <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#E30613] mb-3">More From VLMAG</p>
      <h3 className="font-display uppercase leading-[0.9] tracking-tight text-3xl sm:text-4xl md:text-5xl mb-8 md:mb-10">Beyond The Networks</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        {MORE.map((c) => (
          <motion.div key={c.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <MoreCard c={c} />
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
